
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Search, 
  User, 
  ShoppingCart, 
  Tag, 
  Clock, 
  CreditCard, 
  DollarSign,
  Plus,
  Minus,
  X,
  FileText,
  UserPlus,
  Mail,
  Printer
} from "lucide-react";

// Sample product data
const products = [
  { id: 1, code: "P001", name: "Rice 5kg", price: 25.50, category: "Groceries", hasPromo: true, hasSubsidy: true },
  { id: 2, code: "P002", name: "Cooking Oil 1L", price: 9.90, category: "Groceries", hasPromo: false, hasSubsidy: true },
  { id: 3, code: "P003", name: "Chicken Eggs (Grade A)", price: 12.80, category: "Fresh", hasPromo: true, hasSubsidy: false },
  { id: 4, code: "P004", name: "Milo 1kg", price: 23.50, category: "Beverages", hasPromo: false, hasSubsidy: false },
  { id: 5, code: "P005", name: "Bread", price: 3.90, category: "Bakery", hasPromo: true, hasSubsidy: false },
];

// Sample categories
const categories = [
  { id: "all", name: "All", icon: <ShoppingCart className="h-4 w-4" /> },
  { id: "favorites", name: "Favorites", icon: <Tag className="h-4 w-4" /> },
  { id: "groceries", name: "Groceries", icon: <ShoppingCart className="h-4 w-4" /> },
  { id: "fresh", name: "Fresh", icon: <ShoppingCart className="h-4 w-4" /> },
  { id: "beverages", name: "Beverages", icon: <ShoppingCart className="h-4 w-4" /> },
  { id: "bakery", name: "Bakery", icon: <ShoppingCart className="h-4 w-4" /> },
];

// Payment types
const paymentTypes = [
  { id: "cash", name: "Cash" },
  { id: "ewallet", name: "E-wallet" },
  { id: "duitnow", name: "DuitNow QR" },
  { id: "online", name: "Online (FPX)" },
  { id: "voucher", name: "Voucher" },
  { id: "subsidy", name: "Subsidy" },
  { id: "credit", name: "Credit" },
];

const POS = () => {
  const [barcode, setBarcode] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [orderType, setOrderType] = useState("walkin");
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [orderStatus, setOrderStatus] = useState("new");
  const [paymentType, setPaymentType] = useState("cash");
  const [cartItems, setCartItems] = useState([
    { id: 1, code: "P001", name: "Rice 5kg", price: 25.50, quantity: 2, discount: 2.50, hasPromo: true, hasSubsidy: true },
    { id: 3, code: "P003", name: "Chicken Eggs (Grade A)", price: 12.80, quantity: 1, discount: 0, hasPromo: true, hasSubsidy: false },
  ]);
  
  // Calculate order summary
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const totalDiscount = cartItems.reduce((sum, item) => sum + item.discount, 0);
  const serviceFee = 5.00;
  const tax = (subtotal - totalDiscount + serviceFee) * 0.06;
  const rounding = 0.40;
  const grandTotal = subtotal - totalDiscount + serviceFee + tax + rounding;
  const amountDue = grandTotal;
  const received = 200;
  const changeDue = received - amountDue;

  // Handle barcode input
  const handleBarcodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would search for the product and add it to cart
    console.log("Searching for barcode:", barcode);
    setBarcode("");
  };

  // Handle quantity change
  const handleQuantityChange = (id: number, change: number) => {
    setCartItems(cartItems.map(item => 
      item.id === id ? { ...item, quantity: Math.max(1, item.quantity + change) } : item
    ));
  };

  // Handle remove item
  const handleRemoveItem = (id: number) => {
    setCartItems(cartItems.filter(item => item.id !== id));
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-3xl font-bold text-[#333333]">Point of Sale</h1>
        <p className="text-muted-foreground">Process transactions and manage orders</p>
      </div>

      {/* Header Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Order Type Toggle */}
        <Card className="shadow-sm border-[#8CC84B]/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Order Type</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="walkin" className="w-full" onValueChange={setOrderType}>
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="walkin">Walk-In</TabsTrigger>
                <TabsTrigger value="online">Jimatshop Online</TabsTrigger>
                <TabsTrigger value="preorder">Jimatshop Preorder</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardContent>
        </Card>

        {/* Customer Info */}
        <Card className="shadow-sm border-[#8CC84B]/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Customer</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5 text-[#4D9E41]" />
                <span className="text-sm font-medium">Walk-in Customer</span>
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">Regular</span>
              </div>
              <Button variant="outline" className="text-xs h-8" size="sm">
                <Search className="h-3.5 w-3.5 mr-1" />
                Find Customer (F3)
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Order Status */}
        <Card className="shadow-sm border-[#8CC84B]/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Order Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between">
              <div className="flex items-center space-x-2">
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">New</span>
              </div>
              <div className="flex space-x-1">
                <Button variant="outline" size="sm" className="text-xs h-8">
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  New
                </Button>
                <Button variant="outline" size="sm" className="text-xs h-8">
                  <Clock className="h-3.5 w-3.5 mr-1" />
                  Hold
                </Button>
                <Button variant="outline" size="sm" className="text-xs h-8">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content - 70/30 Split */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Panel - 70% */}
        <div className="md:col-span-2 space-y-4">
          {/* Product Input Section */}
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardContent className="pt-4">
              <form onSubmit={handleBarcodeSubmit} className="flex items-center space-x-2">
                <Input
                  type="text"
                  placeholder="Scan barcode or enter product code"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  autoFocus
                  className="flex-1"
                />
                <Button type="submit">
                  <Search className="h-4 w-4 mr-2" />
                  Find Product (F5)
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Categories */}
          <div className="bg-white p-2 border rounded-md border-[#8CC84B]/20">
            <div className="flex space-x-1 overflow-x-auto pb-2">
              {categories.map((category) => (
                <Button 
                  key={category.id}
                  variant={activeCategory === category.id ? "default" : "outline"}
                  size="sm"
                  className={`flex items-center space-x-1 ${activeCategory === category.id ? 'bg-[#8CC84B]' : ''}`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.icon}
                  <span>{category.name}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Order List Grid */}
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Current Order</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cartItems.map((item) => (
                      <tr key={item.id}>
                        <td className="px-3 py-3 whitespace-nowrap text-sm">
                          {item.code}
                          {item.hasPromo && <span className="inline-block ml-1 px-1.5 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">Promo</span>}
                          {item.hasSubsidy && <span className="inline-block ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">Subsidy</span>}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm">{item.name}</td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm">
                          <div className="flex items-center space-x-1">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-6 w-6 p-0" 
                              onClick={() => handleQuantityChange(item.id, -1)}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-8 text-center">{item.quantity}</span>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-6 w-6 p-0" 
                              onClick={() => handleQuantityChange(item.id, 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm">RM {item.price.toFixed(2)}</td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm">
                          {item.discount > 0 && `RM ${item.discount.toFixed(2)}`}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                          RM {((item.price * item.quantity) - item.discount).toFixed(2)}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveItem(item.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - 30% */}
        <div className="space-y-4">
          {/* Customer Finance Panel */}
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Customer Finance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm">Promotions:</span>
                <span className="text-sm">2 Active</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Vouchers:</span>
                <span className="text-sm">RM 10.00</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Store Credit:</span>
                <span className="text-sm">RM 0.00</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Subsidies:</span>
                <span className="text-sm">RM 50.00</span>
              </div>
            </CardContent>
          </Card>

          {/* Payment Section */}
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Payment Type</CardTitle>
            </CardHeader>
            <CardContent>
              <select 
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value)}
              >
                {paymentTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </CardContent>
          </Card>

          {/* Transaction Summary */}
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Transaction Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1 font-mono text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>RM {subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-red-600">
                  <span>Discount:</span>
                  <span>-RM {totalDiscount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Service Fee:</span>
                  <span>+RM {serviceFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax:</span>
                  <span>+RM {tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Rounding:</span>
                  <span>RM {rounding.toFixed(2)}</span>
                </div>
                <div className="border-t border-dashed border-gray-300 my-2"></div>
                <div className="flex justify-between font-bold">
                  <span>Grand Total:</span>
                  <span>RM {grandTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Amount Due:</span>
                  <span>RM {amountDue.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Received:</span>
                  <span>RM {received.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span>Change Due:</span>
                  <span>RM {changeDue.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Actions */}
          <div className="grid grid-cols-2 gap-2">
            <Button className="bg-[#4D9E41]">
              <CreditCard className="mr-2 h-4 w-4" />
              Pay (F4)
            </Button>
            <Button variant="outline">
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
          </div>

          {/* Footer Controls */}
          <div className="grid grid-cols-3 gap-2">
            <Button variant="outline" size="sm">
              <Printer className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm">
              <Mail className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm">
              <FileText className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POS;
